import { useAnimations } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { ITochiAnimation } from "@snackpass/snackpass-types";
import React, { useEffect, useRef } from "react";
import {
  AnimationAction,
  AnimationClip,
  AnimationObjectGroup,
  LoopOnce,
  LoopRepeat,
} from "three";

type Props = {
  anims: { [name: string]: ITochiAnimation };
  active: { name: string; updated: Date };
  clips: AnimationClip[];
  animGroup: React.MutableRefObject<AnimationObjectGroup>;
  version: string;
  handleEvent: (event: string) => void;
};

const ANIM_FRAME_RATE = 30;

export const AnimationController = ({
  anims,
  active,
  animGroup,
  clips,
  version,
  handleEvent,
}: Props) => {
  const { actions, mixer } = useAnimations(clips, animGroup as any);
  const current = useRef<string | null>(null);
  const prevFrame = useRef<number>(0);

  const handleMixerFinished = () => {
    // sometimes randomly play a different idle one-shot
    const rand = Math.floor(Math.random() * 11);
    const randIdle = rand <= 7 ? "idle" : "idle2";
    play(randIdle, randIdle === "idle");
  };

  useEffect(() => {
    if (!mixer) return;
    // play idle when one-shot is finished
    mixer.addEventListener("finished", handleMixerFinished);
    return () => mixer.removeEventListener("finished", handleMixerFinished);
  }, [mixer]);

  const play = (name: string, loop: boolean) => {
    if (!name || !actions[name]) return;
    const currentAnimation = current.current;
    var to = actions[name] as AnimationAction;

    to.reset();
    if (loop) {
      to.setLoop(LoopRepeat, Infinity);
      to.clampWhenFinished = false;
    } else {
      to.setLoop(LoopOnce, 0);
      to.clampWhenFinished = true;
    }

    if (currentAnimation) {
      var from = actions[currentAnimation] as AnimationAction;

      from.play();
      to.play();

      from.enabled = true;
      to.enabled = true;

      from.crossFadeTo(to, 0.3, true);
    } else {
      to.play();
    }
    current.current = name;
  };

  useEffect(() => {
    if (!actions || !active) return;
    const activeName = actions[active.name] ? active.name : "idle";
    play(activeName, activeName === "idle");
  }, [actions, active]);

  useFrame(() => {
    const currentAnimation = current.current;
    if (!currentAnimation) return;
    var action = actions[currentAnimation] as AnimationAction;
    var anim = (anims[currentAnimation]?.events || ({} as any))[version];
    if (!action || !anim) return;
    const frame = Math.floor(action.time * ANIM_FRAME_RATE);
    if (prevFrame.current > frame) prevFrame.current = 0;
    let event: string[] | undefined;
    // Handle potentially dropped frames
    for (let i = prevFrame.current; i < frame; ++i) {
      if (anim[i.toString()]) event = anim[i.toString()];
    }
    if (event) {
      event.forEach((e) => {
        handleEvent(e);
      });
    }
    prevFrame.current = frame;
  });

  return null;
};
